import { useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useHorizontalOverflow } from '../../../../../../hooks/useHorizontalOverflow/useHorizontalOverflow';
import { ClarityEvents } from '../../../../../../lib/clarity/types/clarityEvents.types';
import { captureEventWithName } from '../../../../../../lib/clarity/utils/captureEvent.utils';
import {
  MORE_PILL_ACTIVE_MAX_WIDTH_PX,
  MORE_PILL_RESERVE_PX,
  TABS_WRAP_BREAKPOINT_PX,
  TAB_GAP_PX,
} from './catalogSectionTabsBar.constants';
import {
  CatalogSectionTabsTrigger,
  UseCatalogSectionTabsBarParams,
} from './catalogSectionTabsBar.types';

export const useCatalogSectionTabsBar = ({
  sections,
  activeSectionId,
  canManage,
  onSelect,
}: UseCatalogSectionTabsBarParams) => {
  const isWrapped = useMediaQuery(`(max-width:${TABS_WRAP_BREAKPOINT_PX - 0.5}px)`, {
    noSsr: true,
  });

  const [isActiveSectionHidden, setIsActiveSectionHidden] = useState(false);

  const recalculationKey = sections.map((section) => `${section.id}:${section.name}`).join('|');

  const { containerRef, measureRef, visibleCount, hiddenCount } = useHorizontalOverflow({
    itemCount: sections.length,
    recalculationKey,
    gap: TAB_GAP_PX,
    minVisible: 1,
    trailer: { mode: 'static', reservePx: 0 },
    extraReservePx: isActiveSectionHidden ? MORE_PILL_ACTIVE_MAX_WIDTH_PX : MORE_PILL_RESERVE_PX,
  });

  const isScrollable = isWrapped;
  const activeSectionIndex = sections.findIndex((section) => section.id === activeSectionId);
  const hasHiddenActiveSection = !isScrollable && activeSectionIndex >= visibleCount;

  if (hasHiddenActiveSection !== isActiveSectionHidden) {
    setIsActiveSectionHidden(hasHiddenActiveSection);
  }

  const handleSelect = (sectionId: string) => {
    captureEventWithName(ClarityEvents.OBJECT_SCREEN_CHANGED_TAB_WITH_NAME, sectionId);
    onSelect(sectionId);
  };

  const resolveTrigger = (): CatalogSectionTabsTrigger => {
    if (isScrollable) {
      return CatalogSectionTabsTrigger.NONE;
    }

    if (hiddenCount > 0) {
      return CatalogSectionTabsTrigger.MORE;
    }

    return canManage ? CatalogSectionTabsTrigger.PLUS : CatalogSectionTabsTrigger.NONE;
  };

  const visibleSections = isScrollable ? sections : sections.slice(0, visibleCount);

  return {
    isScrollable,
    containerRef,
    measureRef,
    visibleSections,
    activeHiddenSection: hasHiddenActiveSection ? sections[activeSectionIndex] : null,
    trigger: resolveTrigger(),
    handleSelect,
  };
};
